import { ref, computed } from 'vue'
import type { SidebarConfig } from '@ai/ui-components'

export type NavigationPermissions = Partial<Record<string, boolean>>

const navigationPermissions = ref<NavigationPermissions>({})

export const useNavigationPermissions = () => {
  /**
   * Set navigation permissions (typically after login)
   */
  const setPermissions = (permissions: NavigationPermissions) => {
    navigationPermissions.value = permissions
  }

  /**
   * Clear all permissions
   */
  const clearPermissions = () => {
    navigationPermissions.value = {}
  }

  /**
   * Check if a specific navigation item is allowed
   */
  const hasPermission = (key: string): boolean => {
    return navigationPermissions.value[key] ?? false
  }

  /**
   * Filter sidebar config based on permissions
   */
  const filterSidebarConfig = (config: SidebarConfig): SidebarConfig => {
    return config
      .map((section) => {
        // Static sections (like Home) are always visible
        if (section.type === 'static') {
          return section
        }

        // For sections with items, filter the items
        if ('items' in section && Array.isArray(section.items)) {
          const filteredItems = section.items.filter((item) => {
            if (item.type === 'static') return true
            return hasPermission(item.key)
          })
          // If no items remain after filtering, exclude the entire section
          if (filteredItems.length === 0) {
            return null
          }

          return {
            ...section,
            items: filteredItems,
          }
        }

        return section
      })
      .filter((section) => section !== null)
  }

  /**
   * Get the filtered sidebar config as a computed property
   */
  const getFilteredConfig = (baseConfig: SidebarConfig) => {
    return computed(() => filterSidebarConfig(baseConfig))
  }

  return {
    permissions: computed(() => navigationPermissions.value),
    setPermissions,
    clearPermissions,
    hasPermission,
    filterSidebarConfig,
    getFilteredConfig,
  }
}
